import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../../api/contentStudioV2';
import AICoachInsight from '../components/AICoachInsight';
import { useProgressWrapper } from '../../../utils/withProgress';
import { BACKEND_URL } from '../../../utils/backendUrl';

// PART E: Frontend Handling - Standardized Error Format
const extractErrMsg = (err) => {
  try {
    if (!err) return { message: 'Unknown error', code: null, meta: null };
    
    // Handle new standardized error format: { code, message, meta }
    if (err.detail && typeof err.detail === 'object') {
      const detail = err.detail;
      return {
        message: detail.message || 'Unknown error',
        code: detail.code || null,
        meta: detail.meta || null
      };
    }
    
    // Legacy format
    if (typeof err === 'string') return { message: err, code: null, meta: null };
    if (err.message) return { message: err.message, code: null, meta: null };
    if (err.detail) return { message: err.detail, code: null, meta: null };
    
    return { message: JSON.stringify(err), code: null, meta: null };
  } catch {
    return { message: 'Failed to parse error', code: null, meta: null };
  }
};

const Step6SplitTest = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [expandedVariants, setExpandedVariants] = useState({}); // Track expanded state per variant
  const withProgress = useProgressWrapper();
  
  // Script editing state
  const [editingVariant, setEditingVariant] = useState(null); // Which variant is being edited
  const [editedSections, setEditedSections] = useState([]); // Edited sections data
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadOrGenerateScripts = async () => {
      // DIAGNOSTIC: Log what data source we're checking
      console.log('=== [STEP 9 DIAGNOSTICS] Script Loading Debug ===');
      console.log('[Step9] Session ID:', sessionId);
      console.log('[Step9] Has sessionData?', !!sessionData);
      console.log('[Step9] Has sessionData.script_variants?', !!sessionData?.script_variants);
      
      // First check if we have scripts in session data (passed from parent)
      if (sessionData?.script_variants && sessionData.script_variants.length > 0) {
        console.log('[Step9] 📦 Using CACHED scripts from session');
        console.log('[Step9] Format used:', sessionData.selected_format?.format_name || 'Unknown');
        console.log('[Step9] Variant count:', sessionData.script_variants.length);
        
        // DIAGNOSTIC: Check structure of first variant
        const firstVariant = sessionData.script_variants[0];
        console.log('[Step9] First variant structure check:');
        console.log('  - variant_id:', firstVariant.variant_id);
        console.log('  - Has sections array?', !!firstVariant.sections);
        console.log('  - Has debug_payload?', !!firstVariant.debug_payload);
        
        if (firstVariant.sections && firstVariant.sections.length > 0) {
          const firstSection = firstVariant.sections[0];
          console.log('  - First section title:', firstSection.title);
          console.log('  - First section has lines?', !!firstSection.lines);
          if (firstSection.lines && firstSection.lines.length > 0) {
            console.log('  - First line preview:', firstSection.lines[0].substring(0, 50) + '...');
          }
        } else {
          console.warn('  - ⚠️ NO sections array! This is OLD format!');
        }
        
        if (firstVariant.debug_payload) {
          console.log('  - Performance mode:', firstVariant.debug_payload.performance_mode);
        } else {
          console.warn('  - ⚠️ NO debug_payload! This is OLD format without slang!');
        }
        
        setVariants(sessionData.script_variants);
        console.log('=== [STEP 9 DIAGNOSTICS] Using sessionData variants ===\n');
        return;
      }
      
      // If not in sessionData, try fetching from backend
      console.log('[Step9] 🔍 Checking backend for existing variants');
      
      try {
        // Fetch session from backend to check if scripts exist
        const response = await fetch(`${BACKEND_URL}/api/v2/content/session/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const sessionFromBackend = await response.json();
          console.log('[Step9] Backend response received');
          console.log('[Step9] Has script_variants in backend?', !!sessionFromBackend.script_variants);
          
          if (sessionFromBackend.script_variants && sessionFromBackend.script_variants.length > 0) {
            console.log('[Step9] ✅ Found CACHED scripts in backend session');
            console.log('[Step9] Format used:', sessionFromBackend.selected_format?.format_name || 'Unknown');
            console.log('[Step9] Variant count:', sessionFromBackend.script_variants.length);
            
            // DIAGNOSTIC: Check structure
            const firstVariant = sessionFromBackend.script_variants[0];
            console.log('[Step9] Backend variant structure:');
            console.log('  - Has sections array?', !!firstVariant.sections);
            console.log('  - Has debug_payload?', !!firstVariant.debug_payload);
            
            setVariants(sessionFromBackend.script_variants);
            console.log('=== [STEP 9 DIAGNOSTICS] Using backend variants ===\n');
            return;
          }
        }
      } catch (err) {
        console.warn('[Step9] Failed to fetch session from backend:', err);
      }
      
      // No existing scripts found, generate new ones
      console.log('[Step9] 🔄 No existing variants found - generating NEW scripts');
      console.log('=== [STEP 9 DIAGNOSTICS] Generating fresh scripts ===\n');
      generateScripts();
    };
    
    loadOrGenerateScripts();
  }, []);

  const generateScripts = async () => {
    setError(null);
    setErrorDetail(null);
    
    // Log request details for debugging
    console.log('[Step8 Script] Generating scripts:', {
      sessionId,
      token: token ? 'present' : 'missing',
      soaSessionId: sessionData?.soaSessionId
    });
    
    try {
      const result = await withProgress(
        {
          label: 'Generating scripts',
          phases: [
            'Preparing format',
            'Drafting Variant A',
            'Drafting Variant B', 
            'Finalizing scripts'
          ],
          canCancel: false,
          timeoutMs: 90000, // Increased to 90s for complex scripts (was 40s)
          meta: {
            session_id: sessionId,
            feature: 'content_studio_step8_generate_scripts'
          }
        },
        async (signal) => {
          return await api.generateScripts(token, sessionId, sessionData.soaSessionId);
        }
      );
      
      console.log('[Step9] ✅ Generation successful!');
      console.log('[Step9] Variant count:', result.variants?.length);
      console.log('[Step9] Variant IDs:', result.variants?.map(v => v.variant_id));
      
      // DIAGNOSTIC: Check structure of generated variants
      if (result.variants && result.variants.length > 0) {
        const firstVariant = result.variants[0];
        console.log('[Step9] Generated variant structure:');
        console.log('  - variant_id:', firstVariant.variant_id);
        console.log('  - Has sections array?', !!firstVariant.sections);
        console.log('  - Section count:', firstVariant.sections?.length);
        console.log('  - Has debug_payload?', !!firstVariant.debug_payload);
        
        if (firstVariant.sections && firstVariant.sections.length > 0) {
          const firstSection = firstVariant.sections[0];
          console.log('  - First section:', firstSection.title);
          if (firstSection.lines && firstSection.lines.length > 0) {
            console.log('  - First line:', firstSection.lines[0].substring(0, 60) + '...');
          }
        }
        
        if (firstVariant.debug_payload) {
          console.log('  - Performance mode:', firstVariant.debug_payload.performance_mode);
        }
      }
      
      setVariants(result.variants || []);
    } catch (err) {
      console.error('[Step8 Script] Error generating scripts:', err);
      console.error('[Step8 Script] Error details:', {
        message: err.message,
        detail: err.detail,
        status: err.status,
        stack: err.stack
      });
      
      const errInfo = extractErrMsg(err);
      console.log('[Step8 Script] Extracted error info:', errInfo);
      
      // Display user-friendly error
      let displayMsg = `Failed to generate scripts: ${errInfo.message}`;
      if (errInfo.meta) {
        displayMsg += ` — ${JSON.stringify(errInfo.meta)}`;
      }
      setError(displayMsg);
      setErrorDetail(errInfo);
    }
  };

  const handleSelect = async () => {
    if (!selected) return;
    try {
      await withProgress(
        {
          label: 'Selecting script',
          phases: ['Saving selection', 'Finalizing'],
          canCancel: false,
          timeoutMs: 5000,
          meta: { session_id: sessionId, variant_id: selected }
        },
        async (signal) => {
          return await api.selectScript(token, sessionId, selected, '');
        }
      );
      
      const result = await api.selectScript(token, sessionId, selected, '');
      onComplete({ selectedScript: result.selected_script, selectedVariant: selected });
    } catch (err) {
      console.error('[Step8 Script] Error selecting script:', err);
      const errInfo = extractErrMsg(err);
      setError(`Failed to select script: ${errInfo.message}`);
    }
  };

  // Start editing a variant
  const handleStartEdit = (variantId) => {
    const variant = variants.find(v => v.variant_id === variantId);
    if (variant && variant.sections) {
      // Deep copy sections for editing
      setEditedSections(JSON.parse(JSON.stringify(variant.sections)));
      setEditingVariant(variantId);
      // Auto-expand the variant being edited
      setExpandedVariants(prev => ({ ...prev, [variantId]: true }));
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingVariant(null);
    setEditedSections([]);
  };

  // Update a specific line in a section
  const handleLineChange = (sectionIdx, lineIdx, newValue) => {
    setEditedSections(prev => {
      const updated = [...prev];
      updated[sectionIdx] = {
        ...updated[sectionIdx],
        lines: updated[sectionIdx].lines.map((line, idx) => 
          idx === lineIdx ? newValue : line
        )
      };
      return updated;
    });
  };

  // Add a new line to a section
  const handleAddLine = (sectionIdx) => {
    setEditedSections(prev => {
      const updated = [...prev];
      updated[sectionIdx] = {
        ...updated[sectionIdx],
        lines: [...updated[sectionIdx].lines, '']
      };
      return updated;
    });
  };

  // Delete a line from a section
  const handleDeleteLine = (sectionIdx, lineIdx) => {
    setEditedSections(prev => {
      const updated = [...prev];
      updated[sectionIdx] = {
        ...updated[sectionIdx],
        lines: updated[sectionIdx].lines.filter((_, idx) => idx !== lineIdx)
      };
      return updated;
    });
  };

  // Save edited script
  const handleSaveEdit = async () => {
    if (!editingVariant || editedSections.length === 0) return;
    
    setIsSaving(true);
    try {
      // Update the variant in local state
      setVariants(prev => prev.map(v => {
        if (v.variant_id === editingVariant) {
          return {
            ...v,
            sections: editedSections,
            is_edited: true, // Mark as edited
            edited_at: new Date().toISOString()
          };
        }
        return v;
      }));
      
      // Save to backend
      await api.updateScriptVariant(token, sessionId, editingVariant, editedSections);
      
      console.log('[Step9 Script] Script saved successfully');
      setEditingVariant(null);
      setEditedSections([]);
    } catch (err) {
      console.error('[Step9 Script] Error saving edited script:', err);
      setError('Failed to save script changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // B) Update Back button to navigate to Step 7 and resume at last question
  const handleBackToSOA = () => {
    console.log('[Step8 Script] Navigating back to SOA Drill');
    
    // Get last answered question index from sessionStorage (default to Q5 if none)
    const lastIndex = Number(sessionStorage.getItem(`soa.${sessionId}.lastIndex`) ?? 4);
    
    console.log('[Step8 Script] Resume index:', lastIndex);
    
    // Navigate back using onBack() which is the proper way in this wizard
    // The parent component (ContentStudioV2) manages step state
    if (onBack) {
      onBack();
    }
  };

  const handleReturnToSOA = () => {
    console.log('[Step8 Script] Error requires SOA completion - returning to Step 7');
    
    // Navigate back using onBack()
    if (onBack) {
      onBack();
    }
  };

  // Generate dynamic title based on variant count
  const getVariantLabel = () => {
    if (variants.length === 0) return 'Split-Test Scripts';
    if (variants.length === 1) return 'Script';
    if (variants.length === 2) return 'Split-Test Scripts (A/B)';
    return 'Split-Test Scripts (A/B/C)';
  };

  return (
    <div style={{ 
      background: '#fff', 
      borderRadius: '12px', 
      padding: '20px',
      paddingBottom: `calc(80px + env(safe-area-inset-bottom, 80px))`, // Aggressive padding for mobile
      marginBottom: 'calc(20px + env(safe-area-inset-bottom, 20px))' // Additional margin
    }}>
      <h2 style={{ fontSize: '20px', color: '#333', marginBottom: '16px' }}>{getVariantLabel()}</h2>
      
      {/* Configuration Summary Panel */}
      {sessionData && (
        <div style={{
          background: 'linear-gradient(135deg, #E0F2FE 0%, #DBEAFE 100%)',
          border: '1px solid #0EA5E9',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px'
        }}>
          <div style={{ 
            fontSize: '12px', 
            fontWeight: 700, 
            color: '#0369A1', 
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            ⚙️ Configuration Applied from Step 6
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '8px',
            fontSize: '13px'
          }}>
            {/* Platform & Duration */}
            {sessionData.platformContext && (
              <>
                <div>
                  <span style={{ color: '#64748B', fontWeight: 600 }}>Platform:</span>{' '}
                  <span style={{ color: '#0F172A' }}>
                    {sessionData.platformContext.channels?.join(', ') || 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{ color: '#64748B', fontWeight: 600 }}>Duration:</span>{' '}
                  <span style={{ color: '#0F172A' }}>
                    {(sessionData.platformContext.normalized_seconds || sessionData.platformContext.normalizedSeconds || sessionData.platformDuration) ? 
                      `${sessionData.platformContext.normalized_seconds || sessionData.platformContext.normalizedSeconds || sessionData.platformDuration}s` : 'N/A'}
                  </span>
                </div>
              </>
            )}
            <div>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Proof Mode:</span>{' '}
              <span style={{ color: '#0F172A' }}>
                {sessionData.evidenceMode || 'Hybrid'}
              </span>
            </div>
            <div>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Language:</span>{' '}
              <span style={{ color: '#0F172A' }}>
                {sessionData.soaPlan?.routing?.language || sessionData.soa_v2?.routing?.language || 'EN'}
              </span>
            </div>
            {/* Slang display removed - slang layer disabled */}
            <div>
              <span style={{ color: '#64748B', fontWeight: 600 }}>Tone:</span>{' '}
              <span style={{ color: '#0F172A' }}>
                {sessionData.soaPlan?.routing?.tone || sessionData.audience?.tone_preference || 'Santai+'}
              </span>
            </div>
          </div>
          {sessionData.knowledge_weights && (
            <div style={{ 
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(14, 165, 233, 0.2)'
            }}>
              <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px', fontWeight: 600 }}>
                Spider Web Influence:
              </div>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '6px',
                fontSize: '11px'
              }}>
                {sessionData.knowledge_weights.product_core && (
                  <span style={{
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#1E40AF',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    Product: {sessionData.knowledge_weights.product_core}%
                  </span>
                )}
                {sessionData.knowledge_weights.promotion && (
                  <span style={{
                    background: 'rgba(249, 115, 22, 0.1)',
                    color: '#C2410C',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    Promo: {sessionData.knowledge_weights.promotion}%
                  </span>
                )}
                {sessionData.knowledge_weights.company_values && (
                  <span style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    color: '#15803D',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontWeight: 600
                  }}>
                    Values: {sessionData.knowledge_weights.company_values}%
                  </span>
                )}
              </div>
            </div>
          )}
          {/* SOA Drill Answers Summary */}
          {sessionData.soa_v2 && sessionData.soa_v2.answers && sessionData.soa_v2.answers.length > 0 && (
            <div style={{ 
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(14, 165, 233, 0.2)'
            }}>
              <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px', fontWeight: 600 }}>
                SOA Drill Answers (Step 7):
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                fontSize: '11px',
                color: '#334155',
                maxHeight: '120px',
                overflowY: 'auto',
                padding: '6px',
                background: 'rgba(14, 165, 233, 0.05)',
                borderRadius: '4px'
              }}>
                {sessionData.soa_v2.answers.slice(0, 5).map((answer, idx) => (
                  <div key={`item-${idx}`} style={{ 
                    padding: '4px 6px',
                    background: '#fff',
                    borderRadius: '3px',
                    border: '1px solid rgba(14, 165, 233, 0.1)'
                  }}>
                    <strong style={{ color: '#0369A1' }}>Q{answer.question}:</strong>{' '}
                    <span>{answer.answer?.substring(0, 80)}{answer.answer?.length > 80 ? '...' : ''}</span>
                    <span style={{ marginLeft: '6px', color: '#059669', fontWeight: 600 }}>
                      ({answer.score}/10)
                    </span>
                  </div>
                ))}
                {sessionData.soa_v2.answers.length > 5 && (
                  <div style={{ fontSize: '10px', color: '#64748B', fontStyle: 'italic', textAlign: 'center', marginTop: '2px' }}>
                    +{sessionData.soa_v2.answers.length - 5} more answers
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Selected Format Display */}
          {sessionData.selectedFormat && (
            <div style={{ 
              marginTop: '10px',
              paddingTop: '10px',
              borderTop: '1px solid rgba(14, 165, 233, 0.2)'
            }}>
              <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px', fontWeight: 600 }}>
                Selected Format (Step 8):
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(247, 148, 30, 0.1)',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(247, 148, 30, 0.3)'
              }}>
                <span style={{ fontSize: '18px' }}>
                  {sessionData.selectedFormat.icon || '🎬'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>
                    {sessionData.selectedFormat.format_name}
                  </div>
                  {(sessionData.selectedFormat.snapshot?.structure_template || sessionData.selectedFormat.structure) && (
                    <div style={{ fontSize: '10px', color: '#64748B', fontFamily: 'monospace', marginTop: '2px' }}>
                      {sessionData.selectedFormat.snapshot?.structure_template || sessionData.selectedFormat.structure}
                    </div>
                  )}
                  {sessionData.selectedFormat.pack_version && (
                    <div style={{ fontSize: '9px', color: '#94A3B8', marginTop: '2px' }}>
                      v{sessionData.selectedFormat.pack_version}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#F7941E',
                  background: 'rgba(247, 148, 30, 0.15)',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}>
                  {Math.round((sessionData.selectedFormat.confidence || 0.85) * 100)}% Match
                </span>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Show error with CTA if SOA incomplete */}
      {errorDetail && (errorDetail.code === 'SOA_INCOMPLETE' || errorDetail.code === 'SOA_BELOW_THRESHOLD') && (
        <div style={{ background: '#FFF4E5', border: '1px solid #F7941E', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: '#D97706', marginBottom: '8px' }}>⚠️ SOA Drill Incomplete</div>
          <div style={{ fontSize: '13px', color: '#92400E', marginBottom: '12px' }}>
            {errorDetail.message}
            {errorDetail.meta && (
              <div style={{ marginTop: '8px', fontSize: '12px', background: '#FEF3C7', padding: '8px', borderRadius: '4px' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(errorDetail.meta, null, 2)}</pre>
              </div>
            )}
          </div>
          <button 
            onClick={handleReturnToSOA}
            style={{ padding: '10px 16px', background: '#F7941E', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
          >
            Return to SOA Drill
          </button>
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        {variants.map(v => {
          const isExpanded = expandedVariants[v.variant_id] || false;
          
          return (
            <div key={v.variant_id} onClick={() => setSelected(v.variant_id)} style={{ padding: '14px', border: selected === v.variant_id ? '2px solid #0e67a2' : '1px solid #ddd', borderRadius: '10px', background: selected === v.variant_id ? '#E8F4FF' : '#fff', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#333' }}>Variant {v.variant_id}</div>
                <div style={{ fontSize: '13px', padding: '4px 8px', background: '#F7941E', color: '#fff', borderRadius: '4px' }}>AI Score: {v.ai_score}/100</div>
              </div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}><strong>Style:</strong> {v.style}</div>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '6px' }}><strong>Focus:</strong> {v.focus}</div>
              
              {/* Full Script Display - Expandable */}
              <div style={{
                fontSize: '13px',
                color: '#333',
                background: '#f5f5f5',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '8px',
                position: 'relative',
                maxHeight: isExpanded ? 'none' : '100px',
                overflow: isExpanded ? 'visible' : 'hidden',
                transition: 'max-height 0.3s ease',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                userSelect: 'text', // Make copyable
                cursor: 'text'
              }}>
                {/* Edit Mode - Editable Script */}
                {editingVariant === v.variant_id ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {editedSections.map((section, sectionIdx) => (
                      <div key={sectionIdx} style={{ 
                        background: '#fff', 
                        border: '1px solid #ddd', 
                        borderRadius: '8px', 
                        padding: '12px' 
                      }}>
                        <div style={{ 
                          fontSize: '12px', 
                          fontWeight: 700, 
                          color: '#0e67a2', 
                          marginBottom: '10px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>
                            {section.title}
                            {section.duration && (
                              <span style={{ marginLeft: '8px', fontSize: '10px', color: '#666', fontWeight: 600 }}>
                                [{section.duration}s]
                              </span>
                            )}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAddLine(sectionIdx); }}
                            style={{
                              padding: '4px 8px',
                              fontSize: '11px',
                              background: '#10B981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            + Add Line
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {section.lines && section.lines.map((line, lineIdx) => (
                            <div key={lineIdx} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                              <textarea
                                value={line}
                                onChange={(e) => handleLineChange(sectionIdx, lineIdx, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  flex: 1,
                                  padding: '8px 10px',
                                  fontSize: '13px',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  resize: 'vertical',
                                  minHeight: '40px',
                                  fontFamily: 'inherit',
                                  lineHeight: '1.5'
                                }}
                                rows={2}
                              />
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteLine(sectionIdx, lineIdx); }}
                                style={{
                                  padding: '8px',
                                  fontSize: '14px',
                                  background: '#FEE2E2',
                                  color: '#DC2626',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                                title="Delete line"
                              >
                                🗑️
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* View Mode - Read-only Script */
                  <>
                    {/* Render from sections array (NEW format) or fall back to script_text (OLD format) */}
                    {v.sections && v.sections.length > 0 ? (
                      v.sections.map((section, idx) => (
                        <div key={`item-${idx}`} style={{ marginBottom: idx < v.sections.length - 1 ? '12px' : 0 }}>
                          <div style={{ 
                            fontSize: '12px', 
                            fontWeight: 700, 
                            color: '#0e67a2', 
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {section.title}
                            {section.duration && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '10px', 
                                color: '#666',
                                fontWeight: 600 
                              }}>
                                [{section.duration}s]
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.6' }}>
                            {section.lines && section.lines.map((line, lineIdx) => (
                              <div key={lineIdx} style={{ marginBottom: '4px' }}>
                                {line}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      // Fallback to old script_text format
                      v.script_text || 'No script content available'
                    )}
                  </>
                )}
                
                {/* Gradient fade at bottom when collapsed */}
                {!isExpanded && !editingVariant && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    background: 'linear-gradient(to bottom, transparent, #f5f5f5)',
                    pointerEvents: 'none'
                  }} />
                )}
              </div>
              
              {/* Edit/Save Buttons */}
              {selected === v.variant_id && v.sections && v.sections.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  {editingVariant === v.variant_id ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSaveEdit(); }}
                        disabled={isSaving}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          background: isSaving ? '#94A3B8' : '#10B981',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: isSaving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {isSaving ? '💾 Saving...' : '💾 Save Changes'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                        disabled={isSaving}
                        style={{
                          padding: '10px 16px',
                          background: '#fff',
                          border: '1px solid #DC2626',
                          borderRadius: '6px',
                          color: '#DC2626',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStartEdit(v.variant_id); }}
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: '#fff',
                        border: '1px solid #F7941E',
                        borderRadius: '6px',
                        color: '#F7941E',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      ✏️ Edit Script
                    </button>
                  )}
                </div>
              )}
              
              {/* Edited badge */}
              {v.is_edited && (
                <div style={{
                  fontSize: '11px',
                  color: '#059669',
                  background: '#D1FAE5',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'inline-block',
                  marginBottom: '8px'
                }}>
                  ✓ Manually edited
                </div>
              )}
              
              {/* Expand/Collapse Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent variant selection
                  setExpandedVariants(prev => ({
                    ...prev,
                    [v.variant_id]: !prev[v.variant_id]
                  }));
                }}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: '#fff',
                  border: '1px solid #0e67a2',
                  borderRadius: '6px',
                  color: '#0e67a2',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <span>{isExpanded ? '▲' : '▼'}</span>
                <span>{isExpanded ? 'Show Less' : 'Show Full Script'}</span>
              </button>
              
              <div style={{ fontSize: '11px', color: '#999', marginTop: '6px' }}>{v.ai_justification}</div>
              
              {/* Slang Debug Display - REMOVED (slang layer disabled) */}
              
              {/* AI Coach Insights */}
              {v.coach_insights && (
                <AICoachInsight 
                  coachInsights={v.coach_insights}
                  aiScore={v.ai_score}
                />
              )}
            </div>
          );
        })}
      </div>
      
      <div style={{ 
        display: 'flex', 
        gap: '12px',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 60px))', // Generous bottom padding
        marginBottom: '20px' // Extra visibility margin
      }}>
        <button onClick={handleBackToSOA} style={{ flex: 1, padding: '14px', background: '#fff', border: '2px solid #0e67a2', borderRadius: '12px', color: '#0e67a2', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }}>Back</button>
        <button onClick={handleSelect} disabled={!selected} style={{ flex: 2, padding: '14px', background: selected ? '#F7941E' : '#ccc', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: selected ? 'pointer' : 'not-allowed' }}>Continue</button>
      </div>
    </div>
  );
};

export default Step6SplitTest;